// =====================================================================
//  ADMIN CONSOLE, ORGANIZATION
// ---------------------------------------------------------------------
//  Org-level structure and the administrator's own account.
//
//  Departments are real rows (migration 0007), with a code, a cost centre,
//  an optional parent for nesting and a department head. They can be
//  created before anyone is assigned to them, and a rename is a single
//  update, the cached copy of the name on each employee record is kept in
//  step by a database trigger.
//
//  Before that migration this screen derived departments with a GROUP BY on
//  the free-text `users.department` column, which meant a department only
//  existed once somebody was assigned that exact string, and renaming one
//  was a sequential bulk write across every affected employee record.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  Building2,
  Check,
  IndianRupee,
  KeyRound,
  Layers,
  MapPin,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Chip,
  ConfirmDialog,
  Field,
  Grid,
  Input,
  KeyValue,
  KpiTile,
  Meter,
  Modal,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  SegmentedControl,
  Select,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
} from '../ui';
import { fmtMoney, fmtNumber, fmtPercent } from '../format';

export default function OrganizationPage({ can, currentUser }) {
  const queryClient = useQueryClient();
  // Reshaping the org chart is a distinct right from editing a person:
  // renaming a department rewrites the cached name on everyone in it.
  const canManage = can('org.manage');
  const [tab, setTab] = useState('profile');

  const { data: overview } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => backendApi.admin.overview(),
  });

  const { data: departments = [], isLoading: deptLoading } = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: () => backendApi.admin.departments(),
  });

  const { data: me } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => backendApi.auth.me(),
  });

  const profile = me?.user || me || currentUser;
  const headcount = overview?.headcount;

  return (
    <div className="ac-page">
      <PageHeader
        title="Organization"
        sub="Your organization's structure, cost profile, and your own administrator account."
      />

      <Tabs
        items={[
          { key: 'profile', label: 'Overview' },
          { key: 'departments', label: 'Departments', count: departments.length },
          { key: 'structure', label: 'Designations, locations & grades' },
          { key: 'account', label: 'My account' },
        ]}
        value={tab}
        onChange={setTab}
      />

      <div style={{ marginTop: 14 }}>
        {tab === 'profile' ? (
          <OrgOverview overview={overview} profile={profile} departments={departments} />
        ) : tab === 'departments' ? (
          <DepartmentsTab
            departments={departments}
            loading={deptLoading}
            canManage={canManage}
            totalHeadcount={headcount?.total || 0}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ['admin'] })}
          />
        ) : tab === 'structure' ? (
          <StructureTab
            canManage={canManage}
            canSeeBands={can('salary_band.read')}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ['admin'] })}
          />
        ) : (
          // Keyed on the resolved profile id so the form remounts (and picks
          // up its initial values) once `auth.me()` comes back, rather than
          // staying stuck on the cached user it first rendered with.
          <AccountTab
            key={profile?.id || 'pending'}
            profile={profile}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['admin', 'me'] })}
          />
        )}
      </div>
    </div>
  );
}

/* =====================================================================
   OVERVIEW
   ================================================================== */

function OrgOverview({ overview, profile, departments }) {
  const headcount = overview?.headcount;
  const payroll = overview?.payroll;

  return (
    <>
      <Grid cols={4} style={{ marginBottom: 14 }}>
        <KpiTile label="Total people" value={fmtNumber(headcount?.total)} icon={Users} foot={`${headcount?.active || 0} active`} />
        <KpiTile
          label="Monthly salary cost"
          value={fmtMoney((headcount?.annualSalaryCost || 0) / 12, { compact: true })}
          icon={IndianRupee}
          foot="Sum of active salaries"
        />
        <KpiTile
          label="Annual salary cost"
          value={fmtMoney(headcount?.annualSalaryCost, { compact: true })}
          icon={IndianRupee}
          foot="Monthly × 12"
        />
        <KpiTile
          label="Average salary"
          value={fmtMoney(headcount?.averageSalary, { compact: true })}
          icon={Users}
          foot="Per active employee"
        />
      </Grid>

      <Grid cols={2}>
        <Panel>
          <PanelHead title="Organization" sub="Details of your tenant on this platform" />
          <PanelBody>
            <KeyValue
              items={[
                { key: 'Organization', value: profile?.tenant_name || 'Your organization' },
                { key: 'Tenant ID', value: profile?.tenant_id ? `#${profile.tenant_id}` : null },
                { key: 'Departments', value: fmtNumber(departments.length) },
                { key: 'Total people', value: fmtNumber(headcount?.total) },
                { key: 'Active', value: fmtNumber(headcount?.active) },
                { key: 'Inactive', value: fmtNumber(headcount?.inactive) },
                { key: 'Joined this month', value: fmtNumber(headcount?.joinedThisMonth) },
                {
                  key: 'Last payroll',
                  value: payroll?.hasRunThisMonth
                    ? `${payroll.month} ${payroll.year} · ${fmtMoney(payroll.netCurrent)}`
                    : 'Not run this month',
                },
              ]}
            />
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Access distribution" sub="How privilege is spread across your people" />
          <PanelBody>
            {!headcount ? null : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                {[
                  { key: 'admin', label: 'Administrators', value: headcount.byRole.admin },
                  { key: 'hr', label: 'HR', value: headcount.byRole.hr },
                  { key: 'manager', label: 'Managers', value: headcount.byRole.manager },
                  { key: 'employee', label: 'Employees', value: headcount.byRole.employee },
                ].map((row) => (
                  <div key={row.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: 'var(--ac-body)' }}>{row.label}</span>
                      <span className="ac-num" style={{ fontWeight: 650, color: 'var(--ac-ink)' }}>
                        {row.value}
                      </span>
                    </div>
                    <Meter value={row.value} max={headcount.total || 1} />
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <Notice tone="info" icon={ShieldCheck}>
                Keep the number of administrators small. Every admin can change payroll, remove people, and approve
                access.
              </Notice>
            </div>
          </PanelBody>
        </Panel>
      </Grid>
    </>
  );
}

/* =====================================================================
   DEPARTMENTS
   ================================================================== */

/**
 * Departments, now backed by a real `departments` table (migration 0007)
 * rather than derived with a GROUP BY on the free-text `users.department`.
 *
 * Two things this changes for the user:
 *
 *   - A department can be CREATED, and exists before anyone is in it.
 *     Previously a department came into being only when someone was
 *     assigned that exact string, so you could not set up a structure in
 *     advance, and a typo silently created a new department.
 *
 *   - A rename is one row update. It used to mean reading up to 1000
 *     employee records and writing each one back individually, because
 *     there was no department row to rename, the name lived on every
 *     employee. A database trigger now propagates the new name to the
 *     cached copy on each employee, so the client does nothing.
 */
function DepartmentsTab({ departments, loading, canManage, totalHeadcount, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', cost_centre: '', parent_id: '', head_user_id: '' });
  const [formError, setFormError] = useState('');

  // Only needed to populate the "department head" picker.
  const { data: people } = useQuery({
    queryKey: ['admin', 'people-lite'],
    queryFn: () => backendApi.entities.Employee.page({ limit: 500, sort: 'full_name' }),
    enabled: canManage,
  });
  const peopleOptions = useMemo(
    () => (people?.rows || []).map((p) => ({ value: String(p.id), label: p.full_name || p.email })),
    [people]
  );

  const openCreate = () => {
    setForm({ name: '', code: '', cost_centre: '', parent_id: '', head_user_id: '' });
    setFormError('');
    setEditing({ isNew: true });
  };

  const openEdit = (row) => {
    setForm({
      name: row.department || '',
      code: row.code || '',
      cost_centre: row.cost_centre || '',
      parent_id: row.parentId ? String(row.parentId) : '',
      head_user_id: '',
    });
    setFormError('');
    setEditing(row);
  };

  const save = useMutation({
    mutationFn: async (payload) => {
      // Blank selects must be sent as null, not '', or the integer foreign
      // key rejects them.
      const body = {
        name: payload.name.trim(),
        code: payload.code.trim() || null,
        cost_centre: payload.cost_centre.trim() || null,
        parent_id: payload.parent_id ? Number(payload.parent_id) : null,
        head_user_id: payload.head_user_id ? Number(payload.head_user_id) : null,
      };

      return editing?.isNew
        ? backendApi.entities.Department.create(body)
        : backendApi.entities.Department.update(editing.id, body);
    },
    onSuccess: () => {
      toast.success(editing?.isNew ? 'Department created' : 'Department updated');
      setEditing(null);
      onChanged();
    },
    onError: (err) => setFormError(err.message || 'Could not save the department'),
  });

  const remove = useMutation({
    mutationFn: (row) => backendApi.entities.Department.delete(row.id),
    onSuccess: () => {
      // Employees keep their records; the foreign key is SET NULL, so they
      // simply become unassigned rather than being deleted.
      toast.success('Department removed. Anyone in it is now unassigned.');
      setPendingDelete(null);
      onChanged();
    },
    onError: (err) => toast.error(err.message || 'Could not remove the department'),
  });

  const columns = useMemo(
    () => [
      {
        key: 'department',
        label: 'Department',
        width: 240,
        render: (row) => (
          <span style={{ fontWeight: 600, color: 'var(--ac-ink)' }}>
            {row.department}
            {row.id === null ? (
              <Chip tone="warning" style={{ marginLeft: 8 }}>
                No department set
              </Chip>
            ) : null}
            {row.parentName ? (
              <span style={{ fontWeight: 400, color: 'var(--ac-muted)' }}> · in {row.parentName}</span>
            ) : null}
          </span>
        ),
        csv: (row) => row.department,
      },
      {
        key: 'code',
        label: 'Code',
        width: 90,
        render: (row) => row.code || ',',
      },
      {
        key: 'headName',
        label: 'Head',
        width: 150,
        render: (row) => row.headName || ',',
      },
      { key: 'headcount', label: 'People', width: 90, align: 'right' },
      { key: 'active', label: 'Active', width: 90, align: 'right' },
      { key: 'leads', label: 'Leads', width: 90, align: 'right', render: (row) => row.leads || ',' },
      {
        key: 'share',
        label: 'Share',
        width: 140,
        align: 'right',
        render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
            <span style={{ width: 56 }}>
              <Meter value={row.headcount} max={totalHeadcount || 1} />
            </span>
            <span className="ac-num">{fmtPercent(row.share, { decimals: 0 })}</span>
          </span>
        ),
        csv: (row) => row.share,
      },
      {
        key: 'averageSalary',
        label: 'Avg salary',
        width: 120,
        align: 'right',
        render: (row) => fmtMoney(row.averageSalary),
        csv: (row) => row.averageSalary,
      },
      {
        key: 'salaryTotal',
        label: 'Monthly cost',
        width: 130,
        align: 'right',
        render: (row) => <strong>{fmtMoney(row.salaryTotal)}</strong>,
        csv: (row) => row.salaryTotal,
      },
    ],
    [totalHeadcount]
  );

  // The synthetic "Unassigned" bucket has no id, so it cannot be edited or
  // deleted, it is a view of people with no department, not a department.
  const isRealDepartment = (row) => row.id !== null && row.id !== undefined;

  // A department cannot be its own parent, and offering the row being
  // edited as its own parent is the easiest way to let someone try.
  const parentOptions = useMemo(
    () =>
      departments
        .filter((row) => isRealDepartment(row) && row.id !== editing?.id)
        .map((row) => ({ value: String(row.id), label: row.department })),
    [departments, editing]
  );

  return (
    <>
      <div style={{ marginTop: 14 }}>
        <DataTable
          columns={columns}
          rows={departments}
          loading={loading}
          getRowId={(row) => row.id ?? 'unassigned'}
          defaultSort={{ key: 'headcount', dir: 'desc' }}
          paginate={false}
          emptyIcon={Building2}
          emptyTitle="No departments yet"
          emptyText="Create a department to start building out the structure."
          rowActions={
            canManage
              ? (row) =>
                  isRealDepartment(row) ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Pencil}
                        aria-label={`Edit ${row.department}`}
                        onClick={() => openEdit(row)}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Trash2}
                        aria-label={`Remove ${row.department}`}
                        onClick={() => setPendingDelete(row)}
                      />
                    </div>
                  ) : null
              : undefined
          }
          toolbar={
            <Toolbar>
              <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>
                {departments.filter(isRealDepartment).length} department
                {departments.filter(isRealDepartment).length === 1 ? '' : 's'} across{' '}
                {fmtNumber(totalHeadcount)} people
              </span>
              <ToolbarSpacer />
              {canManage ? (
                <Button variant="primary" icon={Plus} onClick={openCreate}>
                  Add department
                </Button>
              ) : null}
            </Toolbar>
          }
        />
      </div>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.isNew ? 'Add a department' : `Edit “${editing?.department}”`}
        size="sm"
        footer={
          <>
            <Button onClick={() => setEditing(null)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={Check}
              loading={save.isPending}
              disabled={!form.name.trim()}
              onClick={() => save.mutate(form)}
            >
              {editing?.isNew ? 'Create' : 'Save'}
            </Button>
          </>
        }
      >
        {formError ? (
          <Notice tone="negative" title="Could not save">
            {formError}
          </Notice>
        ) : null}

        <Field label="Name" required>
          <Input
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            autoFocus
          />
        </Field>
        <Grid cols={2}>
          <Field label="Code" hint="Short identifier, e.g. ENG">
            <Input
              value={form.code}
              onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
            />
          </Field>
          <Field label="Cost centre">
            <Input
              value={form.cost_centre}
              onChange={(event) => setForm((prev) => ({ ...prev, cost_centre: event.target.value }))}
            />
          </Field>
        </Grid>
        <Field label="Sits inside" hint="Leave empty for a top-level department">
          <Select
            options={parentOptions}
            placeholder="No parent"
            value={form.parent_id}
            onChange={(event) => setForm((prev) => ({ ...prev, parent_id: event.target.value }))}
          />
        </Field>
        <Field label="Department head">
          <Select
            options={peopleOptions}
            placeholder="Not set"
            value={form.head_user_id}
            onChange={(event) => setForm((prev) => ({ ...prev, head_user_id: event.target.value }))}
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate(pendingDelete)}
        loading={remove.isPending}
        title={`Remove “${pendingDelete?.department}”?`}
        confirmLabel="Remove department"
        message={
          pendingDelete?.headcount
            ? `${pendingDelete.headcount} ${
                pendingDelete.headcount === 1 ? 'person is' : 'people are'
              } in this department. Their records are kept, they simply become unassigned.`
            : 'Nobody is in this department, so nothing else is affected.'
        }
      />
    </>
  );
}

/* =====================================================================
   DESIGNATIONS, LOCATIONS AND GRADES
   ---------------------------------------------------------------------
   The rest of the org structure from migration 0007. Grouped onto one
   tab rather than three because they are small, related reference lists
   that are set up once and rarely touched, three nav entries for three
   short tables would be out of proportion.

   Grades carry a salary band, which is restricted separately from the
   grade itself: everyone may see that a "Senior Engineer" band exists,
   while what it pays needs salary_band.read. The server redacts the
   figures regardless, so this only avoids rendering an empty column that
   would look like missing data.
   ================================================================== */

const STRUCTURE_KINDS = {
  designation: {
    entity: 'Designation',
    label: 'Job title',
    plural: 'Job titles',
    icon: BadgeCheck,
    empty: 'Job titles are what appears as a person’s position on their record.',
  },
  location: {
    entity: 'Location',
    label: 'Location',
    plural: 'Locations',
    icon: MapPin,
    empty: 'Locations let the holiday calendar differ between offices.',
  },
  grade: {
    entity: 'Grade',
    label: 'Grade',
    plural: 'Grades',
    icon: Layers,
    empty: 'Grades group job titles into levels and carry a salary band.',
  },
};

function StructureTab({ canManage, canSeeBands, onChanged }) {
  const [kind, setKind] = useState('designation');
  const [form, setForm] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [formError, setFormError] = useState('');

  const config = STRUCTURE_KINDS[kind];

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'structure', kind],
    queryFn: () => backendApi.entities[config.entity].page({ limit: 300 }),
  });
  const rows = useMemo(() => data?.rows || [], [data]);

  // Grades are needed as options when editing a job title, so they are
  // fetched regardless of which sub-tab is showing.
  const { data: gradeData } = useQuery({
    queryKey: ['admin', 'structure', 'grade'],
    queryFn: () => backendApi.entities.Grade.page({ limit: 300 }),
  });
  const gradeOptions = useMemo(
    () =>
      (gradeData?.rows || []).map((g) => ({
        value: String(g.id),
        label: g.level != null ? `${g.name} (level ${g.level})` : g.name,
      })),
    [gradeData]
  );

  const save = useMutation({
    mutationFn: (values) => {
      const body = { name: values.name.trim(), code: values.code?.trim() || null };

      if (kind === 'designation') {
        body.grade_id = values.grade_id ? Number(values.grade_id) : null;
      }
      if (kind === 'location') {
        Object.assign(body, {
          city: values.city?.trim() || null,
          state: values.state?.trim() || null,
          country: values.country?.trim() || null,
          timezone: values.timezone?.trim() || null,
        });
      }
      if (kind === 'grade') {
        Object.assign(body, {
          level: values.level ? Number(values.level) : null,
          min_salary: values.min_salary ? Number(values.min_salary) : null,
          max_salary: values.max_salary ? Number(values.max_salary) : null,
        });
      }

      return form?.id
        ? backendApi.entities[config.entity].update(form.id, body)
        : backendApi.entities[config.entity].create(body);
    },
    onSuccess: () => {
      toast.success(form?.id ? `${config.label} updated` : `${config.label} created`);
      setForm(null);
      onChanged();
    },
    onError: (err) => setFormError(err.message || 'Could not save this'),
  });

  const remove = useMutation({
    mutationFn: (row) => backendApi.entities[config.entity].delete(row.id),
    onSuccess: () => {
      toast.success('Removed');
      setPendingDelete(null);
      onChanged();
    },
    onError: (err) => toast.error(err.message || 'Could not remove this'),
  });

  const columns = useMemo(() => {
    const base = [
      {
        key: 'name',
        label: config.label,
        width: 240,
        render: (row) => (
          <span style={{ fontWeight: 600, color: 'var(--ac-ink)' }}>
            {row.name}
            {row.is_active === false ? (
              <Chip tone="negative" style={{ marginLeft: 8 }}>
                Inactive
              </Chip>
            ) : null}
          </span>
        ),
        csv: (row) => row.name,
      },
      {
        key: 'code',
        label: 'Code',
        width: 110,
        render: (row) => row.code || <span style={{ color: 'var(--ac-faint)' }}>,</span>,
      },
    ];

    if (kind === 'designation') {
      base.push({
        key: 'grade_id',
        label: 'Grade',
        render: (row) =>
          gradeOptions.find((g) => g.value === String(row.grade_id))?.label || (
            <span style={{ color: 'var(--ac-faint)' }}>Not graded</span>
          ),
        csv: (row) => row.grade_id || '',
      });
    }

    if (kind === 'location') {
      base.push(
        {
          key: 'city',
          label: 'Where',
          render: (row) =>
            [row.city, row.state, row.country].filter(Boolean).join(', ') || (
              <span style={{ color: 'var(--ac-faint)' }}>,</span>
            ),
          csv: (row) => [row.city, row.state, row.country].filter(Boolean).join(', '),
        },
        {
          key: 'timezone',
          label: 'Timezone',
          width: 170,
          render: (row) =>
            row.timezone ? (
              <span className="ac-mono">{row.timezone}</span>
            ) : (
              // Without one, a punch cannot be reliably assigned to a day.
              <Chip tone="warning">Not set</Chip>
            ),
        }
      );
    }

    if (kind === 'grade') {
      base.push(
        { key: 'level', label: 'Level', width: 90, align: 'right' },
        {
          key: 'band',
          label: 'Salary band',
          align: 'right',
          sortable: false,
          render: (row) => {
            if (!canSeeBands) return <span style={{ color: 'var(--ac-faint)' }}>Restricted</span>;
            if (row.min_salary == null && row.max_salary == null) {
              return <span style={{ color: 'var(--ac-faint)' }}>,</span>;
            }
            return (
              <span className="ac-mono">
                {fmtMoney(row.min_salary)}, {fmtMoney(row.max_salary)}
              </span>
            );
          },
          csv: (row) => (canSeeBands ? `${row.min_salary ?? ''}-${row.max_salary ?? ''}` : ''),
        }
      );
    }

    return base;
  }, [kind, config.label, gradeOptions, canSeeBands]);

  const openCreate = () => {
    setFormError('');
    setForm({
      name: '',
      code: '',
      grade_id: '',
      city: '',
      state: '',
      country: '',
      timezone: '',
      level: '',
      min_salary: '',
      max_salary: '',
    });
  };

  const openEdit = (row) => {
    setFormError('');
    setForm({
      id: row.id,
      name: row.name || '',
      code: row.code || '',
      grade_id: row.grade_id ? String(row.grade_id) : '',
      city: row.city || '',
      state: row.state || '',
      country: row.country || '',
      timezone: row.timezone || '',
      level: row.level == null ? '' : String(row.level),
      min_salary: row.min_salary == null ? '' : String(row.min_salary),
      max_salary: row.max_salary == null ? '' : String(row.max_salary),
    });
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <SegmentedControl
          items={Object.entries(STRUCTURE_KINDS).map(([key, meta]) => ({
            key,
            label: meta.plural,
          }))}
          value={kind}
          onChange={(next) => {
            setKind(next);
            setForm(null);
          }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowId={(row) => row.id}
        paginate={false}
        emptyIcon={config.icon}
        emptyTitle={`No ${config.plural.toLowerCase()} yet`}
        emptyText={config.empty}
        rowActions={
          canManage
            ? (row) => (
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={Pencil}
                    aria-label={`Edit ${row.name}`}
                    onClick={() => openEdit(row)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={Trash2}
                    aria-label={`Remove ${row.name}`}
                    onClick={() => setPendingDelete(row)}
                  />
                </div>
              )
            : undefined
        }
        toolbar={
          <Toolbar>
            <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>
              {rows.length} {rows.length === 1 ? config.label.toLowerCase() : config.plural.toLowerCase()}
            </span>
            <ToolbarSpacer />
            {canManage ? (
              <Button variant="primary" icon={Plus} onClick={openCreate}>
                Add {config.label.toLowerCase()}
              </Button>
            ) : null}
          </Toolbar>
        }
      />

      <Modal
        open={Boolean(form)}
        onClose={() => setForm(null)}
        title={form?.id ? `Edit “${form.name}”` : `Add a ${config.label.toLowerCase()}`}
        size="sm"
        footer={
          <>
            <Button onClick={() => setForm(null)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={Check}
              loading={save.isPending}
              disabled={!form?.name?.trim()}
              onClick={() => save.mutate(form)}
            >
              Save
            </Button>
          </>
        }
      >
        {formError ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="negative" title="Could not save">
              {formError}
            </Notice>
          </div>
        ) : null}

        {form ? (
          <>
            <Grid cols={2}>
              <Field label="Name" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoFocus
                />
              </Field>
              <Field label="Code">
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </Field>
            </Grid>

            {kind === 'designation' ? (
              <Field label="Grade" hint="Groups this title into a level and salary band">
                <Select
                  options={gradeOptions}
                  placeholder="Not graded"
                  value={form.grade_id}
                  onChange={(e) => setForm({ ...form, grade_id: e.target.value })}
                />
              </Field>
            ) : null}

            {kind === 'location' ? (
              <>
                <Grid cols={2}>
                  <Field label="City">
                    <Input
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                    />
                  </Field>
                  <Field label="State / region">
                    <Input
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                    />
                  </Field>
                </Grid>
                <Grid cols={2}>
                  <Field label="Country">
                    <Input
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                    />
                  </Field>
                  <Field
                    label="Timezone"
                    hint="e.g. Asia/Kolkata. Decides which day a punch belongs to."
                  >
                    <Input
                      value={form.timezone}
                      placeholder="Asia/Kolkata"
                      onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                    />
                  </Field>
                </Grid>
              </>
            ) : null}

            {kind === 'grade' ? (
              <>
                <Field label="Level" hint="Ordinal seniority, used to compare grades">
                  <Input
                    type="number"
                    value={form.level}
                    onChange={(e) => setForm({ ...form, level: e.target.value })}
                  />
                </Field>

                {canSeeBands ? (
                  <>
                    <Grid cols={2}>
                      <Field label="Minimum salary">
                        <Input
                          type="number"
                          min="0"
                          value={form.min_salary}
                          onChange={(e) => setForm({ ...form, min_salary: e.target.value })}
                        />
                      </Field>
                      <Field label="Maximum salary">
                        <Input
                          type="number"
                          min="0"
                          value={form.max_salary}
                          onChange={(e) => setForm({ ...form, max_salary: e.target.value })}
                        />
                      </Field>
                    </Grid>
                    {form.min_salary &&
                    form.max_salary &&
                    Number(form.min_salary) > Number(form.max_salary) ? (
                      <Notice tone="negative">
                        The minimum is above the maximum. The server will reject this.
                      </Notice>
                    ) : null}
                  </>
                ) : (
                  <Notice tone="info">
                    Salary bands are restricted. You can create the grade, but setting what it
                    pays needs the salary band permission.
                  </Notice>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate(pendingDelete)}
        loading={remove.isPending}
        title={`Remove “${pendingDelete?.name || ''}”?`}
        confirmLabel="Remove"
        message={
          kind === 'designation'
            ? 'Anyone holding this job title keeps their record; their position simply becomes unset.'
            : kind === 'location'
              ? 'Holidays tied to this location are removed with it. People assigned to it become unlocated.'
              : 'Job titles on this grade become ungraded. Nobody’s salary changes.'
        }
      />
    </>
  );
}

/* =====================================================================
   MY ACCOUNT
   ================================================================== */

function AccountTab({ profile, onSaved }) {
  const [form, setForm] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    phone: profile?.phone || '',
  });
  const [passwords, setPasswords] = useState({ current_password: '', password: '', confirm: '' });
  const [errors, setErrors] = useState({});

  const setField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const setPassword = (key) => (event) => {
    setPasswords((prev) => ({ ...prev, [key]: event.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const saveProfile = useMutation({
    mutationFn: (payload) => backendApi.auth.updateProfile(payload),
    onSuccess: () => {
      toast.success('Profile updated');
      onSaved();
    },
    onError: (err) => toast.error(err.message || 'Could not update your profile'),
  });

  const changePassword = useMutation({
    mutationFn: (payload) => backendApi.auth.updateProfile(payload),
    onSuccess: () => {
      toast.success('Password changed');
      setPasswords({ current_password: '', password: '', confirm: '' });
    },
    onError: (err) => toast.error(err.message || 'Could not change your password'),
  });

  const submitPassword = (event) => {
    event.preventDefault();
    const next = {};
    if (!passwords.current_password) next.current_password = 'Enter your current password';
    if (!passwords.password) next.password = 'Enter a new password';
    else if (passwords.password.length < 8) next.password = 'Use at least 8 characters';
    if (passwords.password !== passwords.confirm) next.confirm = 'Passwords do not match';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    changePassword.mutate({
      current_password: passwords.current_password,
      password: passwords.password,
    });
  };

  return (
    <Grid cols={2}>
      <Panel>
        <PanelHead title="Your details" sub="How you appear to the rest of the organization" />
        <PanelBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Grid cols={2}>
              <Field label="First name">
                <Input value={form.first_name} onChange={setField('first_name')} />
              </Field>
              <Field label="Last name">
                <Input value={form.last_name} onChange={setField('last_name')} />
              </Field>
            </Grid>
            <Field label="Phone">
              <Input value={form.phone} onChange={setField('phone')} />
            </Field>
            <Field label="Email" hint="Contact the platform owner to change your sign-in email">
              <Input value={profile?.email || ''} disabled />
            </Field>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                icon={UserCog}
                onClick={() => saveProfile.mutate(form)}
                loading={saveProfile.isPending}
              >
                Save details
              </Button>
            </div>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead title="Password" sub="Change the password you use to sign in" />
        <PanelBody>
          <form onSubmit={submitPassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Current password" required error={errors.current_password}>
              <Input
                type="password"
                autoComplete="current-password"
                value={passwords.current_password}
                onChange={setPassword('current_password')}
                invalid={!!errors.current_password}
              />
            </Field>
            <Field label="New password" required error={errors.password} hint="At least 8 characters">
              <Input
                type="password"
                autoComplete="new-password"
                value={passwords.password}
                onChange={setPassword('password')}
                invalid={!!errors.password}
              />
            </Field>
            <Field label="Confirm new password" required error={errors.confirm}>
              <Input
                type="password"
                autoComplete="new-password"
                value={passwords.confirm}
                onChange={setPassword('confirm')}
                invalid={!!errors.confirm}
              />
            </Field>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="primary" icon={KeyRound} loading={changePassword.isPending}>
                Change password
              </Button>
            </div>
          </form>

          <div style={{ marginTop: 16 }}>
            <Notice tone="warning" icon={ShieldCheck}>
              You hold administrator privileges. Use a password you don't reuse anywhere else.
            </Notice>
          </div>
        </PanelBody>
      </Panel>
    </Grid>
  );
}
